import { HomeHeader } from '../home/HomeHeader'
import { useAuth } from '../../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { User, Mail, Calendar, BookOpen, CheckCircle, Clock, XCircle, Trash2 } from 'lucide-react'
import { useState, useEffect } from 'react'
import { apiClient } from '../../lib/api'
import { MembershipCard } from '../membership/MembershipCard'
import { DeleteAccountDialog } from './DeleteAccountDialog'
import { Button } from '../ui/button'
import { useTranslation } from 'react-i18next'
type UserBookSummary = {
  id: string
  translation_count?: number
  available_languages?: string[]
}

export function Profile() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const { t } = useTranslation('common')
  const [books, setBooks] = useState<UserBookSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  useEffect(() => {
    if (!user) return

    loadBooks()
  }, [user])

  const loadBooks = async () => {
    try {
      setLoading(true)
      const response = await apiClient.getUserBooks()
      setBooks(response.books)
    } catch (error) {
      console.error('Error fetching books:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const totalBooks = books.length
  const completedBooks = books.filter(book => (book.translation_count ?? 0) > 0).length
  // Note: Processing and failed book counts would require checking translation status
  // For now, we only show completed count based on translation_count
  const processingBooks = 0 // TODO: Implement based on translation status
  const failedBooks = 0 // TODO: Implement based on translation status

  return (
    <div className="min-h-screen bg-background">
      <HomeHeader user={user} onLogout={handleLogout} />
      
      <div className="container mx-auto px-4 py-6">
        {/* Compact Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-2 h-8 bg-primary rounded-full"></div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Profile</h1>
            <p className="text-sm text-muted-foreground">
              Your account information and reading statistics
            </p>
          </div>
        </div>

        <div className="grid gap-8 lg:grid-cols-3">
          {/* User Information */}
          <div className="lg:col-span-1">
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-xl font-semibold text-foreground mb-6">User Information</h2>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <User className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="font-medium">{user?.name || 'Not provided'}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium">{user?.email || 'Not provided'}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Member Since</p>
                    <p className="font-medium">Recently</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Book Statistics */}
          <div className="lg:col-span-2">
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-xl font-semibold text-foreground mb-6">Book Statistics</h2>
              
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <div className="bg-background border border-border rounded-lg p-4 text-center">
                    <BookOpen className="h-8 w-8 text-primary mx-auto mb-2" />
                    <p className="text-2xl font-bold text-foreground">{totalBooks}</p>
                    <p className="text-sm text-muted-foreground">Total Books</p>
                  </div>
                  
                  <div className="bg-background border border-border rounded-lg p-4 text-center">
                    <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-foreground">{completedBooks}</p>
                    <p className="text-sm text-muted-foreground">Completed</p>
                  </div>
                  
                  <div className="bg-background border border-border rounded-lg p-4 text-center">
                    <Clock className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-foreground">{processingBooks}</p>
                    <p className="text-sm text-muted-foreground">Processing</p>
                  </div>
                  
                  <div className="bg-background border border-border rounded-lg p-4 text-center">
                    <XCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-foreground">{failedBooks}</p>
                    <p className="text-sm text-muted-foreground">Failed</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Membership Section */}
        <div className="mt-8">
          <h2 className="text-xl font-semibold text-foreground mb-4">Membership & Usage</h2>
          <MembershipCard />
        </div>

        {/* Danger Zone - Delete Account */}
        <div className="danger-zone mt-12 border-t border-border pt-8">
          <div className="bg-card border border-destructive/20 rounded-lg p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="space-y-1">
                <h3 className="text-lg font-semibold text-destructive">
                  {t('profile.deleteAccount.title')}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t('profile.deleteAccount.description')}
                </p>
              </div>
              <Button
                variant="outline"
                className="delete-account-btn border-destructive/50 text-destructive hover:bg-destructive hover:text-destructive-foreground shrink-0"
                onClick={() => setShowDeleteDialog(true)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t('profile.deleteAccount.buttonText')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <DeleteAccountDialog 
        open={showDeleteDialog} 
        onOpenChange={setShowDeleteDialog} 
      />
    </div>
  )
}
